import React, { useCallback, useEffect, useState } from "react";
import { Box, ToggleButtonGroup, ToggleButton, Alert, Snackbar } from "@mui/material";
import ViewListIcon from "@mui/icons-material/ViewList";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EquipmentService from "../../../services/Equipment.service";
import EquipmentList from "./EquipmentList";
import EquipmentCalendar from "./EquipmentCalendar";
import EquipmentWeeklyCalendar from "./EquipmentWeeklyCalendar";
import EquipmentMonthlyCalendar from "./EquipmentMonthlyCalendar";
import EquipmentInspectionDialog from "./EquipmentInspectionDialog";
import EquipmentDetailsDialog from "./EquipmentDetailsDialog";
import ResolveTaggedOutModal from "./ResolveTaggedOutModal";
import { getCadence, isInCurrentPeriod, currentPeriodParams } from "./period";

export default function Equipment() {
    const [types, setTypes] = useState([]);
    const [selectedTypeId, setSelectedTypeId] = useState(null);
    const [view, setView] = useState("list");
    const [equipmentList, setEquipmentList] = useState([]);
    const [inspections, setInspections] = useState([]);
    const [issues, setIssues] = useState([]);
    const [loading, setLoading] = useState(false);
    const [checkDialog, setCheckDialog] = useState({ open: false, equipment: null });
    const [detailsDialog, setDetailsDialog] = useState({ open: false, equipment: null });
    const [resolveDialog, setResolveDialog] = useState({ open: false, equipment: null, issue: null });
    const [dataVersion, setDataVersion] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: "" });

    useEffect(() => {
        EquipmentService.listTypes().then((res) => {
            const list = Array.isArray(res.data) ? res.data : res.data.results || [];
            setTypes(list);
            if (list.length > 0) setSelectedTypeId(list[0].id);
        });
    }, []);

    const selectedType = types.find((t) => t.id === selectedTypeId) || null;
    const cadence = getCadence(selectedType);

    const refresh = useCallback(() => {
        if (!selectedTypeId) return;
        setLoading(true);
        const periodParams = currentPeriodParams(cadence);
        Promise.all([
            EquipmentService.listEquipment(selectedTypeId),
            EquipmentService.listInspections({ equipment_type: selectedTypeId, ...periodParams }),
            EquipmentService.listIssues({ equipment_type: selectedTypeId, is_resolved: "false" }),
        ])
            .then(([eqRes, inspRes, issueRes]) => {
                setEquipmentList(Array.isArray(eqRes.data) ? eqRes.data : eqRes.data.results || []);
                setInspections(Array.isArray(inspRes.data) ? inspRes.data : inspRes.data.results || []);
                setIssues(Array.isArray(issueRes.data) ? issueRes.data : issueRes.data.results || []);
            })
            .finally(() => setLoading(false));
    }, [selectedTypeId, cadence]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const isChecked = (equipmentId) =>
        inspections.some((i) => i.equipment === equipmentId && isInCurrentPeriod(i.date, cadence));

    const lastInspectionFor = (equipmentId) => {
        const forEquipment = inspections
            .filter((i) => i.equipment === equipmentId)
            .sort((a, b) => (a.date < b.date ? 1 : -1));
        return forEquipment[0] || null;
    };

    const openIssueFor = (equipmentId) => issues.find((i) => i.equipment === equipmentId) || null;

    const handleOpenDetails = (eq) => setDetailsDialog({ open: true, equipment: eq });
    const handleResolveIssue = (eq, issue) => setResolveDialog({ open: true, equipment: eq, issue });

    return (
        <Box>
            <Box sx={{ display: "flex", gap: 1, mb: 2, alignItems: "center" }}>
                <ToggleButtonGroup
                    value={view}
                    exclusive
                    size="small"
                    onChange={(_, val) => {
                        if (val !== null) setView(val);
                    }}
                >
                    <ToggleButton value="list" sx={{ height: 36 }}>
                        <ViewListIcon fontSize="small" />
                    </ToggleButton>
                    <ToggleButton value="calendar" sx={{ height: 36 }}>
                        <CalendarMonthIcon fontSize="small" />
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {types.length > 0 && (
                <Box sx={{ mb: 2, overflowX: "auto" }}>
                    <ToggleButtonGroup
                        value={selectedTypeId}
                        exclusive
                        size="small"
                        fullWidth
                        onChange={(_, val) => {
                            if (val !== null) setSelectedTypeId(val);
                        }}
                    >
                        {types.map((t) => (
                            <ToggleButton
                                key={t.id}
                                value={t.id}
                                sx={{
                                    whiteSpace: "nowrap",
                                    minWidth: { xs: 0, sm: 120 },
                                    flex: { xs: 1, sm: "initial" },
                                    px: { xs: 0.5, sm: 2 },
                                    height: 36,
                                }}
                            >
                                {t.description}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>
            )}

            {types.length === 0 && !loading && (
                <Alert severity="info">No equipment types have been configured yet.</Alert>
            )}

            {view === "list" ? (
                <EquipmentList
                    equipmentList={equipmentList}
                    loading={loading}
                    cadence={cadence}
                    isChecked={isChecked}
                    lastInspectionFor={lastInspectionFor}
                    openIssueFor={openIssueFor}
                    onStartCheck={(eq) => setCheckDialog({ open: true, equipment: eq })}
                    onOpenDetails={handleOpenDetails}
                    onResolveIssue={handleResolveIssue}
                />
            ) : cadence === "weekly" ? (
                <EquipmentWeeklyCalendar
                    equipmentTypeId={selectedTypeId}
                    equipmentList={equipmentList}
                    refreshSignal={dataVersion}
                    onStartCheck={(eq) => setCheckDialog({ open: true, equipment: eq })}
                    onOpenDetails={handleOpenDetails}
                    onResolveIssue={handleResolveIssue}
                />
            ) : cadence === "monthly" ? (
                <EquipmentMonthlyCalendar
                    equipmentTypeId={selectedTypeId}
                    equipmentList={equipmentList}
                    refreshSignal={dataVersion}
                    onStartCheck={(eq) => setCheckDialog({ open: true, equipment: eq })}
                    onOpenDetails={handleOpenDetails}
                    onResolveIssue={handleResolveIssue}
                />
            ) : (
                <EquipmentCalendar
                    equipmentTypeId={selectedTypeId}
                    equipmentList={equipmentList}
                    refreshSignal={dataVersion}
                    onStartCheck={(eq) => setCheckDialog({ open: true, equipment: eq })}
                    onOpenDetails={handleOpenDetails}
                    onResolveIssue={handleResolveIssue}
                />
            )}

            <EquipmentInspectionDialog
                open={checkDialog.open}
                onClose={() => setCheckDialog({ open: false, equipment: null })}
                equipment={checkDialog.equipment}
                onCompleted={() => {
                    refresh();
                    setDataVersion((v) => v + 1);
                    setSnackbar({ open: true, message: "Inspection submitted." });
                }}
            />

            <EquipmentDetailsDialog
                open={detailsDialog.open}
                onClose={() => setDetailsDialog({ open: false, equipment: null })}
                equipment={detailsDialog.equipment}
            />

            <ResolveTaggedOutModal
                open={resolveDialog.open}
                onClose={() => setResolveDialog({ open: false, equipment: null, issue: null })}
                equipment={resolveDialog.equipment}
                issue={resolveDialog.issue}
                onResolved={() => {
                    refresh();
                    setDataVersion((v) => v + 1);
                    setSnackbar({ open: true, message: "Issue resolved." });
                }}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ open: false, message: "" })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="success" sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
